// Source module import (not the barrel) to avoid a pre-auth import cycle.
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { getAnalyticsMetaProperties } from '@/services/analytics/meta-properties';
import { getOrCreateSignupAnonymousId } from '@/services/signup/anonymous-id';

/**
 * Pre-auth URLs can carry credentials or PII in their query string (login
 * 2FA `token`, the funnels' `email`/`first`/`last` prefills), and step
 * navigation preserves query params — strip them before reporting.
 */
const SENSITIVE_QUERY_PARAMS = ['token', 'email', 'first', 'last'];

function sanitizedLocation(): { url: string; search: string } {
  const url = new URL(window.location.href);
  for (const param of SENSITIVE_QUERY_PARAMS) {
    url.searchParams.delete(param);
  }
  return { url: url.toString(), search: url.search };
}

/**
 * Fire a pre-auth analytics event for anonymous visitors (login, signup).
 * Pre-auth pages can't use Hydra's workspace-scoped `useAnalytics`, so this
 * posts to the global `track_anon` endpoint with the same event names, the
 * standard meta-properties block, the per-event properties, and the `anon_id`
 * (so anonymous sessions stitch to the user on the next `alias`). Failures are
 * swallowed — analytics is best-effort and must never block a pre-auth form.
 */
export function trackAnonEvent(event: string, properties: Record<string, unknown> = {}): void {
  void hydraUiApi.tracking
    .trackAnon({
      body: {
        anon_id: getOrCreateSignupAnonymousId(),
        event,
        properties: { ...getAnalyticsMetaProperties(), ...sanitizedLocation(), ...properties },
      },
    })
    .catch(() => {});
}
